import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { MessageHubService } from './messagehub.service';
import { ServiceInjector } from '../classes/infrastructure/injector';
import { Action1 } from '../classes/infrastructure/domain';
import { IntegerPacket } from '../classes/infrastructure/payloadpacket/integerpacket';

@Injectable({
  providedIn: 'root'
})
export class NotificationManagementService {

  constructor(private messageHub: MessageHubService) { }

  /* 🔹 notification count per user (latest value) */
  private notificationCountMap: { [userRef: string]: number } = {};

  /* 🔹 notification count observable per user */
  private notificationCountSubjectMap: {
    [userRef: string]: BehaviorSubject<number>
  } = {};

  /* 🔹 PID (PublisherId) per user */
  private publisherIdMap: { [userRef: string]: number } = {};

  /* 🔹 PID observable per user */
  private pidSubjectMap: {
    [userRef: string]: BehaviorSubject<number | null>
  } = {};

  /* 🔹 change handlers */
  private notificationChangeHandlers: Map<
    string,
    Map<number, Action1<number>>
  > = new Map();

  private handlerIdCounter = 0;

  /* 🔹 avoid duplicate hub subscriptions */
  private subscribedUsers = new Set<string>();

  /* =======================================================
     Subscribe to Notification Topic
     ======================================================= */
  public SubscribeToNotifications(userRef: number) {
    const key = userRef.toString();
    const topic = `${userRef}/notificationcount`;

    // ✅ prevent duplicate hub subscriptions
    if (this.subscribedUsers.has(key)) {
      return;
    }
    this.subscribedUsers.add(key);

    if (!this.pidSubjectMap[key]) {
      this.pidSubjectMap[key] = new BehaviorSubject<number | null>(null);
    }

    if (!this.notificationCountSubjectMap[key]) {
      this.notificationCountSubjectMap[key] = new BehaviorSubject<number>(0);
    }

    this.messageHub.RegisterIntegerPacketHandler(
      topic,
      (packet: IntegerPacket) => {
        // debugger
        // console.log('📩 Integer packet received:', packet);

        const pid = packet?.PID;
        const count = packet?.V;

        if (pid == null) {
          console.warn('⚠️ PID missing in packet');
          return;
        }
        // console.log('🔥 RAW PACKET:', packet);
        // console.log('🔥 COUNT EMITTED:', packet?.V);
        /* 🔹 PID handling */
        const oldPid = this.publisherIdMap[key];
        if (pid !== oldPid) {
          this.publisherIdMap[key] = pid;
          this.pidSubjectMap[key].next(pid);
        }

        /* 🔹 Notification count handling */
        this.notificationCountMap[key] = count;
        this.notificationCountSubjectMap[key].next(count);

        /* 🔹 Notify registered handlers with COUNT */
        const handlers = this.getChangeHandlers(key);
        handlers.forEach(h => {
          try {
            h(count);
          } catch (ex) {
            console.error('Notification handler error', ex);
          }
        });
      }
    );
  }

  /* =======================================================
     PID Observable
     ======================================================= */
  public getPid$(userRef: number) {
    const key = userRef.toString();

    if (!this.pidSubjectMap[key]) {
      this.pidSubjectMap[key] = new BehaviorSubject<number | null>(null);
    }

    return this.pidSubjectMap[key].asObservable();
  }

  public getPid(userRef: number): number | null {
    return this.publisherIdMap[userRef.toString()] ?? null;
  }

  /* =======================================================
     Notification Count Observable
     ======================================================= */
  public getNotificationCount$(userRef: number) {
    const key = userRef.toString();
    // debugger
    if (!this.notificationCountSubjectMap[key]) {
      this.notificationCountSubjectMap[key] = new BehaviorSubject<number>(0);
    }

    return this.notificationCountSubjectMap[key].asObservable();
  }

  /* =======================================================
     Register / Unregister Change Handlers
     ======================================================= */
  public RegisterNotificationChangeHandler(
    userRef: number,
    handler: Action1<number>
  ): number {
    const key = userRef.toString();
    this.handlerIdCounter++;

    if (!this.notificationChangeHandlers.has(key)) {
      this.notificationChangeHandlers.set(key, new Map());
    }

    this.notificationChangeHandlers
      .get(key)!
      .set(this.handlerIdCounter, handler);

    return this.handlerIdCounter;
  }

  private getChangeHandlers(key: string): Action1<number>[] {
    if (!this.notificationChangeHandlers.has(key)) return [];
    return Array.from(this.notificationChangeHandlers.get(key)!.values());
  }

  /* =======================================================
     Synchronous Getter
     ======================================================= */
  public getNotificationCount(userRef: number): number {
    return this.notificationCountMap[userRef.toString()] || 0;
  }
}
